import {
  PriceUtils,
  ServiceProposalManualAction,
  hasDiscount,
} from "@repo/common"
import { useTotalActionPrice } from "@repo/ui/hooks/use-total-action-price"
import { cn } from "@repo/ui/lib/utils"
import { Lock } from "lucide-react"
import * as React from "react"
import { useTranslations } from "use-intl"
import { OriginalPrice } from "../../components/original-price"
import { ProductOrderingIndicator } from "./product-ordering-indicator"
import {
  SelectableCard,
  SelectableCardContent,
  SelectableCardDescription,
  SelectableCardHeader,
  SelectableCardIndicator,
  SelectableCardLabel,
  SelectableCardPrice,
  SelectableCardSection,
} from "./selectable-card"
import { ServiceProposalManualActionProductChoicesField } from "./service-proposal-manual-action-product-choices-field"

interface ServiceProposalManualActionCardProps {
  data: ServiceProposalManualAction
  onProductSelect?: (productId: string, choiceId: string) => void
  selected: boolean
  onSelect: () => void
  interactive?: boolean
  selectable?: boolean
  className?: string
  productDecisions?: Record<string, string>
  showProductComments?: boolean
}

const ServiceProposalManualActionCard = React.forwardRef<
  HTMLDivElement,
  ServiceProposalManualActionCardProps
>(
  (
    {
      className,
      data,
      onProductSelect,
      selected,
      onSelect,
      interactive = true,
      selectable = true,
      productDecisions = {},
      showProductComments = true,
    },
    ref,
  ) => {
    const t = useTranslations("ui")
    const shouldDimContent = !selected

    // A mandatory manual action is locked: the customer cannot decline it.
    const isLocked = data.isMandatory

    const { total: totalPrice } = useTotalActionPrice({
      ...data,
      productDecisions,
    })

    return (
      <SelectableCard
        ref={ref}
        className={cn(
          className,
          isLocked && "border-secondary-foreground bg-secondary-background",
        )}
        selected={selected}
        onSelect={onSelect}
        interactive={!isLocked && selectable && interactive}
        color="secondary"
        aria-disabled={isLocked || undefined}
      >
        <SelectableCardHeader>
          <div className="flex gap-2 items-center">
            <div className="space-y-1 flex-1">
              <SelectableCardLabel>{data.title}</SelectableCardLabel>
            </div>
          </div>

          {isLocked ? (
            <div className="ml-auto flex flex-shrink-0 items-center gap-1 self-start text-foreground">
              <Lock className="size-4" />
              <span className="text-xs font-medium">
                {t("common.required")}
              </span>
            </div>
          ) : (
            selectable && <SelectableCardIndicator />
          )}
        </SelectableCardHeader>

        <SelectableCardContent>
          <SelectableCardPrice className={shouldDimContent ? "opacity-40" : ""}>
            {PriceUtils.format(totalPrice)}
          </SelectableCardPrice>

          {data.comment && (
            <SelectableCardSection
              className={shouldDimContent ? "opacity-40" : ""}
            >
              <SelectableCardLabel>
                {t("juloActionInput.comment")}
              </SelectableCardLabel>
              <SelectableCardDescription>
                {data.comment}
              </SelectableCardDescription>
            </SelectableCardSection>
          )}

          {data.products.length > 0 && (
            <SelectableCardSection
              className={shouldDimContent ? "opacity-40" : ""}
            >
              <SelectableCardLabel className="-mb-1">
                {t("proposalForm.includedProducts")}
                <span className="float-right">{t("proposalForm.cost")}</span>
              </SelectableCardLabel>
              <div className="space-y-1">
                {data.products.map((product) => {
                  const activeDiscount = hasDiscount(product.discount)
                    ? product.discount
                    : null
                  const discountedPrice = activeDiscount
                    ? PriceUtils.applyDiscount(
                        product.price,
                        product.discount ?? null,
                      )
                    : product.price
                  return (
                    <div key={product.id}>
                      <div className="flex justify-between items-center">
                        <SelectableCardDescription>
                          {product.title}
                        </SelectableCardDescription>
                        {activeDiscount ? (
                          <div className="flex items-center gap-2">
                            <OriginalPrice>
                              {PriceUtils.format(product.price)}
                            </OriginalPrice>
                            <SelectableCardDescription className="text-destructive-foreground">
                              {PriceUtils.format(discountedPrice)}
                            </SelectableCardDescription>
                            <span className="text-xs text-destructive-foreground">
                              {PriceUtils.formatDiscountLabel(activeDiscount)}
                            </span>
                          </div>
                        ) : (
                          <SelectableCardDescription>
                            {PriceUtils.format(product.price)}
                          </SelectableCardDescription>
                        )}
                      </div>
                      {product.needsOrdering && (
                        <ProductOrderingIndicator
                          expectedDeliveryDate={product.expectedDeliveryDate}
                          className="mt-1"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </SelectableCardSection>
          )}

          <ServiceProposalManualActionProductChoicesField
            interactive={interactive}
            productChoices={data.productChoices}
            onProductSelect={onProductSelect}
            shouldDimContent={shouldDimContent}
            productDecisions={productDecisions}
            showComments={showProductComments}
          />
        </SelectableCardContent>
      </SelectableCard>
    )
  },
)

ServiceProposalManualActionCard.displayName = "ServiceProposalManualActionCard"

export {
  ServiceProposalManualActionCard,
  type ServiceProposalManualActionCardProps,
}
